import React, { useEffect, useState } from 'react';  
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';  
import { Bell, ChevronLeft, ChevronRight, User, Menu } from 'lucide-react';  
import { Button } from '../ui/button';  
import { useToast } from '../../hooks/use-toast';  
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';  
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';  
import Logo from '../common/Logo';  
import { auth } from '../../../src/firebase'; // Path to your Firebase config file  
import { signOut } from "firebase/auth";  

/**  
 * The main layout component for the app.  
 */  
export const MainLayout = ({ isLoggedIn = true }) => {  
  const [sidebarOpen, setSidebarOpen] = useState(true);  
  const location = useLocation();  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Initial check for mobile  
  const { toast } = useToast();  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);  
  const navigate = useNavigate(); 
   

  useEffect(() => {  
    const handleResize = () => {  
      setIsMobile(window.innerWidth < 768); // Update isMobile based on current window width  
    };  

    const handleLocationChange = () => {  
      window.scrollTo(0, 0); // Scroll to the top when the location path changes  
    };  

    // Set up event listener for window resize   
    window.addEventListener('resize', handleResize);  

    handleLocationChange();
    // Clean up function to remove event listener on unmount   
    return () => {  
      window.removeEventListener('resize', handleResize);  
    };  
  }, [location]);  

  useEffect(() => {  
    // Automatically open the mobile menu when on mobile  
    setSidebarOpen(!isMobile); // Open sidebar if not mobile, otherwise close it  
  }, [isMobile]);  

  const toggleSidebar = () => {  
    setSidebarOpen(!sidebarOpen);  
  };  

  const toggleMobileMenu = () => {  
    setMobileMenuOpen(!mobileMenuOpen);  
  };  

  const handleLogout = async() => {  
    await signOut(auth);  
    localStorage.clear();  
    navigate('/login');  
    // toast({  
    //   title: "Logged out successfully",  
    //   description: "You have been logged out of your account",  
    // });  
  };   

  // Navigation links with icons and paths  
  const navLinks = [  
    { icon: <DashboardIcon />, title: 'Dashboard', path: '/dashboard' },  
    { icon: <CoursesIcon />, title: 'Courses', path: '/courses' },  
    { icon: <InterviewPrepIcon />, title: 'Interview Prep', path: '/interview-prep' },  
    { icon: <AssessmentsIcon />, title: 'Assessments', path: '/assessments' },  
    { icon: <ContactIcon />, title: 'Contact Us', path: '/contact' },  
  ];  

  return (  
    <>  
      {isLoggedIn && <>  
          <div className="flex min-h-screen bg-gray-50">  
            {/* Desktop Sidebar */}  
            <aside  
              className={`fixed inset-y-0 z-50 flex flex-col bg-bytesplash-primary transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-64' : 'w-20'} ${isMobile ? 'hidden' : 'flex'}`}  
            >  
              <div className="flex items-center bg-white justify-between h-16 px-4 border-b border-bytesplash-dark/20">  
                <div className={`flex items-center ${sidebarOpen ? 'justify-between w-full' : 'justify-center'}`}>  
                  <Logo className="text-white" sidebarOpen={sidebarOpen} />  
                  <button  
                    onClick={toggleSidebar}  
                    className="absolute -right-3 top-20 bg-white text-[#0071bd] rounded-full p-1 shadow-md hover:bg-gray-100 transition-colors"  
                  >  
                    {sidebarOpen ? <ChevronLeft size={15} color="#0071bd" /> : <ChevronRight size={15} color="#0071bd" />}  
                  </button>  
                </div>  
              </div>  

              <nav className="flex-1 overflow-y-auto py-4">  
                <ul className="space-y-1 px-1">  
                  {navLinks.map((link) => (  
                    <li key={link.path}>  
                      <NavLink  
                        to={link.path}  
                        className={({ isActive }) =>  
                          `sidebar-link ${isActive ? 'active' : ''} ${sidebarOpen ? '' : 'justify-center'}`  
                        }  
                      >  
                        <span>{link.icon}</span>  
                        {sidebarOpen && <span>{link.title}</span>}  
                      </NavLink>  
                    </li>  
                  ))}  
                </ul>  
              </nav>  

              <div className="p-4 border-t border-bytesplash-dark/20">  
                <NavLink  
                  to="/profile"  
                  className={({ isActive }) =>  
                    `sidebar-link ${isActive ? 'active' : ''} ${sidebarOpen ? '' : 'justify-center'}`  
                  }  
                >  
                  <User size={20} />  
                  {sidebarOpen && <span>Profile</span>}  
                </NavLink>  
              </div>  
            </aside>  

            {/* Mobile Sidebar */}  
            {mobileMenuOpen && (  
              <div className="fixed inset-0 z-50 lg:hidden">  
                <div className="fixed inset-0 bg-black/50 " onClick={toggleMobileMenu}></div>  
                <aside className="fixed inset-y-0 left-0 w-64 flex flex-col bg-bytesplash-primary animate-slide-in">  
                  <div className="flex items-center bg-white justify-between h-16 px-4 border-b border-bytesplash-dark/20">  
                    <Logo className="text-white" sidebarOpen={true} />  
                    <button  
                      onClick={toggleMobileMenu}  
                      className="rounded-full p-1 hover:bg-bytesplash-dark/20 transition-colors duration-200 text-white"  
                    >  
                      <Menu size={24} />  
                    </button>  
                  </div>  

                  <nav className="flex-1 overflow-y-auto py-4">  
                    <ul className="space-y-1 px-2">  
                      {navLinks.map((link) => (  
                        <li key={link.path}>  
                          <NavLink  
                            to={link.path}  
                            className={({ isActive }) =>  
                              `sidebar-link ${isActive ? 'active' : ''}`  
                            }  
                            onClick={toggleMobileMenu}  
                          >  
                            <span>{link.icon}</span>  
                            <span>{link.title}</span>  
                          </NavLink>  
                        </li>  
                      ))}  
                    </ul>  
                  </nav>  

                  <div className="p-4 border-t border-bytesplash-dark/20">  
                    <NavLink  
                      to="/profile"  
                      className={({ isActive }) =>  
                        `sidebar-link ${isActive ? 'active' : ''}`  
                      }  
                      onClick={toggleMobileMenu}  
                    >  
                      <User size={20} />  
                      <span>Profile</span>  
                    </NavLink>  
                  </div>  
                </aside>  
              </div>  
            )}  

            {/* Main Content */}  
            <main className={`flex-1 transition-all duration-300 ${isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-20')}`}>  
              <header className="sticky top-0 z-40 flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200">  
                {isMobile && (  
                  <button  
                    onClick={toggleMobileMenu}  
                    className="p-2 rounded-md hover:bg-gray-100"  
                  >  
                    <Menu size={24} />  
                  </button>  
                )}  

                <h1  
                  className="flex-1 text-center text-gray-600 font-semibold text-sm sm:text-base md:text-lg lg:text-2xl whitespace-nowrap"  
                  style={{  
                    fontFamily: '"Hanken Grotesk", sans-serif',  
                    fontWeight: 600,  
                  }}  
                >  
                  Learning Management System  
                </h1>  

                <div className="flex items-center space-x-4">  
                  <Button variant="ghost" size="icon" className="relative hover:bg-[#0071bd]">  
                    <Bell size={20} />  
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>  
                  </Button>  

                  <DropdownMenu>  
                    <DropdownMenuTrigger asChild>  
                      <Button variant="ghost" className="relative h-10 w-10 rounded-full">  
                        <Avatar>  
                          <AvatarImage src="" />  
                          <AvatarFallback>U</AvatarFallback>  
                        </Avatar>  
                      </Button>  
                    </DropdownMenuTrigger>  
                    <DropdownMenuContent align="end">  
                      <DropdownMenuItem asChild>  
                        <NavLink to="/profile">Profile</NavLink>  
                      </DropdownMenuItem>  
                      <DropdownMenuItem asChild>  
                        <NavLink to="/profile/change-password">Change Password</NavLink>  
                      </DropdownMenuItem>  
                      <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>  
                    </DropdownMenuContent>  
                  </DropdownMenu>  
                </div>  
              </header>  

              {/* Page Content */}  
              <div className="container mx-auto px-4 py-6 animate-fade-in">  
                <Outlet />  
              </div>  
            </main>  
          </div>  
          <footer className="py-6 text-center text-bytesplash-primary text-sm">  
              &copy; {new Date().getFullYear()} ByteSplash Learning Management System  
          </footer>  
        </>  
      }  
    </>  
  );  
};  

// Example Components for Icons  
const DashboardIcon = () => (  
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-layout-dashboard">  
    <rect width="7" height="9" x="3" y="3" rx="1" />  
    <rect width="7" height="5" x="14" y="3" rx="1" />  
    <rect width="7" height="9" x="14" y="12" rx="1" />  
    <rect width="7" height="5" x="3" y="16" rx="1" />  
  </svg>  
);  

const CoursesIcon = () => (  
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-book-open">  
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />  
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />  
  </svg>  
);  

const InterviewPrepIcon = () => (  
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-briefcase">  
    <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />  
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />  
  </svg>  
);  

const AssessmentsIcon = () => (  
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard-check">  
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />  
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />  
    <path d="m9 14 2 2 4-4" />  
  </svg>  
);  

const ContactIcon = () => (  
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-contact">  
    <path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2" />  
    <rect width="18" height="18" x="3" y="4" rx="2" />  
    <circle cx="12" cy="10" r="2" />  
    <line x1="8" x2="8" y1="2" y2="4" />  
    <line x1="16" x2="16" y1="2" y2="4" />  
  </svg>  
);  

export default MainLayout;  
